/**
 * Public Terms of Service / Privacy Policy pages.
 *
 * Reachable without a session (they're linked from /signup, which is
 * pre-auth) and safe to link from marketing.
 *
 * NOTE FOR THE OPERATOR: the copy below is a good-faith baseline describing
 * how Fleet Iris actually works — it is NOT legal advice and has not been
 * reviewed by counsel. Have a lawyer review and adjust before you rely on it
 * commercially, and fill in the CONTACT/ENTITY placeholders.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const ENTITY = 'Dynamic Leo';
const PRODUCT = 'Fleet Iris';
const CONTACT_EMAIL = 'it@dynamicleo.com';
const LAST_UPDATED = '26 July 2026';

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service">
      <P>
        These terms govern your use of {PRODUCT}, a fleet and field-operations platform provided by{' '}
        {ENTITY}. By creating a workspace you agree to them on behalf of your organisation.
      </P>

      <H>1. Your workspace</H>
      <P>
        A workspace is created when you sign up and is owned by the organisation you name, not by
        the individual who registers it. You are responsible for the accuracy of the data you enter
        and for the actions of every user and field user you grant access to.
      </P>

      <H>2. Acceptable use</H>
      <P>You agree not to:</P>
      <UL
        items={[
          'Use the service to break any applicable law, including labour, privacy and vehicle-tracking law in the territories you operate in.',
          'Track individuals without the notice and consent their local law requires.',
          'Attempt to access another organisation’s workspace, or probe, scan or load-test the service without written permission.',
          'Resell, sublicense or white-label the service without a separate written agreement.',
        ]}
      />

      <H>3. Location data and your workforce</H>
      <P>
        {PRODUCT} records GPS location from the mobile app while a field user is on duty. You are
        the data controller for that information. It is your responsibility to inform your workers
        that tracking occurs, to collect any consent required where they work, and to limit
        collection to working hours.
      </P>

      <H>4. Availability</H>
      <P>
        We aim for continuous availability but do not guarantee uninterrupted service. Planned
        maintenance, third-party outages and force-majeure events may cause downtime. Trial
        workspaces are provided as-is with no availability commitment.
      </P>

      <H>5. Your data</H>
      <P>
        Your operational data remains yours. You can export it at any time from the Reports section.
        If you cancel, we retain the workspace for 30 days so it can be restored, then delete it.
      </P>

      <H>6. Suspension and termination</H>
      <P>
        We may suspend a workspace that is being used in breach of these terms, that is not paying,
        or that threatens the security of the platform. You may cancel at any time from Settings.
      </P>

      <H>7. Liability</H>
      <P>
        To the maximum extent permitted by law, {ENTITY} is not liable for indirect or consequential
        loss, lost profits, or loss of data. {PRODUCT} is an operational tool and is not a
        substitute for your own safety, compliance or payroll controls — always verify
        safety-critical and payroll outputs before acting on them.
      </P>

      <H>8. Changes</H>
      <P>
        We may update these terms. Material changes will be notified in-app or by email to workspace
        owners at least 14 days before they take effect.
      </P>

      <H>9. Contact</H>
      <P>
        Questions about these terms: <Mail />
      </P>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <P>
        This policy explains what {PRODUCT} collects, why, and what control you have over it. It
        covers both workspace administrators and the field users who use the mobile app.
      </P>

      <H>1. What we collect</H>
      <UL
        items={[
          'Account data — name, work email, organisation name, country and time zone.',
          'Field-user records — employee ID, name, phone number, and the documents your organisation uploads.',
          'Location data — GPS position, speed and heading from the mobile app while a field user is on duty.',
          'Operational data — duty sessions, work units, odometer readings, fuel logs, attendance, advances, fines and payroll runs.',
          'Technical data — IP address, device and browser information, and error diagnostics used to keep the service running.',
        ]}
      />

      <H>2. Location tracking</H>
      <P>
        Location is collected only while a field user has started a duty session in the mobile app.
        Ending duty or logging out stops collection. The app shows a persistent indicator while
        tracking is active. Your employer configures retention and can see your position history for
        the periods you were on duty.
      </P>

      <H>3. Why we process it</H>
      <P>
        To provide the service you signed up for: showing assets on the live map, deriving
        attendance from duty sessions, calculating payroll, raising alerts, and supporting you when
        something breaks. We do not sell personal data and we do not use it for advertising.
      </P>

      <H>4. Who can see it</H>
      <P>
        Data is isolated per workspace — no organisation can read another&apos;s. Within your
        workspace, visibility follows the roles and permissions your administrators configure. Our
        engineers access production data only when required to resolve a specific fault, and such
        access is logged.
      </P>

      <H>5. Processors we use</H>
      <UL
        items={[
          'Cloud hosting and managed Postgres for application data.',
          'Object storage for uploaded documents and photos.',
          'A transactional email provider for verification, invitations and password resets.',
          'An error-monitoring provider that receives diagnostic traces.',
        ]}
      />

      <H>6. Retention</H>
      <P>
        Operational records are kept for as long as your workspace is active, then for 30 days after
        cancellation before deletion. Deleted documents and messages are retained in a soft-deleted
        state for 90 days so they can be recovered.
      </P>

      <H>7. Your rights</H>
      <P>
        Depending on where you live you may have the right to access, correct, export or delete your
        personal data, and to object to certain processing. Field users should raise these requests
        with their employer, who controls the workspace; employers can contact us at <Mail /> and we
        will assist.
      </P>

      <H>8. Security</H>
      <P>
        Traffic is encrypted in transit. Passwords and PINs are stored hashed, never in plain text.
        Sessions can be reviewed and revoked from Settings. If we become aware of a breach affecting
        your data we will notify workspace owners without undue delay.
      </P>

      <H>9. Contact</H>
      <P>
        Privacy questions or requests: <Mail />
      </P>
    </LegalShell>
  );
}

// ── Presentation ───────────────────────────────────────────────────────────
function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-6 py-12">
        <Link
          to="/signup"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to sign up
        </Link>

        <h1 className="mt-6 text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {PRODUCT} by {ENTITY} · Last updated {LAST_UPDATED}
        </p>

        <div className="mt-8">{children}</div>

        <div className="mt-12 flex gap-4 border-t border-border pt-6 text-sm">
          <Link to="/terms" className="text-primary hover:underline">
            Terms of Service
          </Link>
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-2 mt-8 text-base font-semibold">{children}</h2>;
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{children}</p>;
}

function UL({ items }: { items: string[] }) {
  return (
    <ul className="mb-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

function Mail() {
  return (
    <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary hover:underline">
      {CONTACT_EMAIL}
    </a>
  );
}
